import React, {memo, useLayoutEffect, useRef, useState} from 'react';
import ReactApexChart from 'react-apexcharts';

const parseHexColor = (value) => {
	if (typeof value !== 'string') {
		return null;
	}

	const normalized = value.trim();

	if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)) {
		return null;
	}

	let hex = normalized.slice(1);

	if (hex.length === 3) {
		hex = hex
			.split('')
			.map((char) => char + char)
			.join('');
	}

	return {
		r: parseInt(hex.slice(0, 2), 16),
		g: parseInt(hex.slice(2, 4), 16),
		b: parseInt(hex.slice(4, 6), 16),
	};
};

const toRgba = (color, alpha) => {
	const parsedColor = parseHexColor(color);

	if (!parsedColor) {
		return color;
	}

	return `rgba(${parsedColor.r}, ${parsedColor.g}, ${parsedColor.b}, ${alpha})`;
};

const BELOW_ZERO_COLOR = '#3b82f6';

// Rozsah číselných hodnot série (null / NaN ignorujeme)
const getValueExtent = (values) => {
	let min = null;
	let max = null;

	(values || []).forEach((value) => {
		const number = parseFloat(value);

		if (!Number.isFinite(number)) {
			return;
		}

		if (min === null || number < min) {
			min = number;
		}

		if (max === null || number > max) {
			max = number;
		}
	});

	return {min, max};
};

// Poloha série vůči nule: celá nad nulou (above), celá pod nulou (below), nebo nulu protíná (split).
// Gradienty ApexCharts mají gradientUnits=objectBoundingBox, tj. 0 % = nejvyšší bod cesty a 100 % = nejnižší bod cesty.
// Křivka 'smooth' i 'straight' nepřekračuje rozsah dat, takže poloha nuly v procentech odpovídá přesně.
const getZeroSplit = ({min, max}) => {
	if (min === null || min >= 0) {
		return {mode: 'above'};
	}

	if (max <= 0) {
		return {mode: 'below'};
	}

	return {mode: 'split', offset: (max / (max - min)) * 100};
};

// Ostrý přechod barvy přesně v místě nuly - pro čáry a pásma s konstantní průhledností
const createSplitColorStops = (offset, aboveColor, belowColor, opacity) => [
	{offset: 0, color: aboveColor, opacity},
	{offset, color: aboveColor, opacity},
	{offset, color: belowColor, opacity},
	{offset: 100, color: belowColor, opacity},
];

// Výplň plochy: nad nulou sytost klesá směrem dolů k nule, pod nulou naopak roste směrem od nuly ke křivce
const createAreaFillColorStops = (split, aboveColor, belowColor, topOpacity, midOpacity) => {
	if (split.mode === 'below') {
		return [
			{offset: 0, color: belowColor, opacity: 0},
			{offset: 45, color: belowColor, opacity: midOpacity},
			{offset: 100, color: belowColor, opacity: topOpacity},
		];
	}

	if (split.mode === 'split') {
		const zero = split.offset;

		return [
			{offset: 0, color: aboveColor, opacity: topOpacity},
			{offset: zero * 0.55, color: aboveColor, opacity: midOpacity},
			{offset: zero, color: aboveColor, opacity: 0},
			{offset: zero, color: belowColor, opacity: 0},
			{offset: zero + (100 - zero) * 0.45, color: belowColor, opacity: midOpacity},
			{offset: 100, color: belowColor, opacity: topOpacity},
		];
	}

	return [
		{offset: 0, color: aboveColor, opacity: topOpacity},
		{offset: 55, color: aboveColor, opacity: midOpacity},
		{offset: 100, color: aboveColor, opacity: 0},
	];
};

const isEvery3HourLabel = (value) => {
	const parts = String(value || '')
		.trim()
		.split(':');

	if (parts.length !== 2 || parts[1] !== '00') {
		return false;
	}

	const hour = parseInt(parts[0], 10);

	return !Number.isNaN(hour) && hour % 3 === 0;
};

const shouldShowEvery3HoursLabel = (index, timestamps, label) => {
	if (timestamps?.length) {
		const date = new Date(timestamps[index]);

		return date.getMinutes() === 0 && date.getHours() % 3 === 0;
	}

	return isEvery3HourLabel(label);
};

const shouldShowEvery6HoursLabel = (index, timestamps) => {
	if (!timestamps?.length) {
		return index === 0;
	}

	const date = new Date(timestamps[index]);

	return date.getMinutes() === 0 && date.getHours() % 6 === 0;
};

const shouldShow25HoursLabel = (index, timestamps) => {
	if (!timestamps?.length) {
		return index === 0;
	}

	if (index === 0 || index === timestamps.length - 1) {
		return true;
	}

	const date = new Date(timestamps[index]);
	const previousDate = new Date(timestamps[index - 1]);

	if (date.getDate() !== previousDate.getDate() || date.getMonth() !== previousDate.getMonth() || date.getFullYear() !== previousDate.getFullYear()) {
		return true;
	}

	return date.getMinutes() === 0 && date.getHours() % 3 === 0;
};

const shouldShowDailyLabel = (index, timestamps) => {
	if (!timestamps?.length) {
		return index === 0;
	}

	if (index === 0) {
		return true;
	}

	const date = new Date(timestamps[index]);
	const previousDate = new Date(timestamps[index - 1]);

	return date.getDate() !== previousDate.getDate() || date.getMonth() !== previousDate.getMonth() || date.getFullYear() !== previousDate.getFullYear();
};

const dayStartMs = (timestamp) => {
	const date = new Date(timestamp);
	date.setHours(0, 0, 0, 0);

	return date.getTime();
};

const shouldShowEvery5DaysLabel = (index, timestamps) => {
	if (!timestamps?.length) {
		return index === 0;
	}

	const startDay = dayStartMs(timestamps[0]);
	const currentDay = dayStartMs(timestamps[index]);
	const daysSinceStart = Math.round((currentDay - startDay) / 86400000);

	if (daysSinceStart % 5 !== 0) {
		return false;
	}

	if (index > 0 && dayStartMs(timestamps[index - 1]) === currentDay) {
		return false;
	}

	return true;
};

const shouldShowMonthlyLabel = (index, timestamps) => {
	if (!timestamps?.length || index === 0) {
		return true;
	}

	const current = new Date(timestamps[index]);
	const previous = new Date(timestamps[index - 1]);

	return current.getMonth() !== previous.getMonth() || current.getFullYear() !== previous.getFullYear();
};

const formatTooltipDateTime = (timestamp, includeSeconds = false) => {
	if (!timestamp) {
		return '';
	}

	const date = new Date(timestamp);

	return date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear() + ' ' + date.toLocaleTimeString('cs-CZ', {hour: '2-digit', minute: '2-digit', second: includeSeconds ? '2-digit' : undefined});
};

const formatShortDateTime = (timestamp) => {
	if (!timestamp) {
		return '';
	}

	const date = new Date(timestamp);

	return date.getDate() + '.' + (date.getMonth() + 1) + '. ' + date.toLocaleTimeString('cs-CZ', {hour: '2-digit', minute: '2-digit'});
};

const formatTimeLabel = (timestamp) => {
	if (!timestamp) {
		return '';
	}

	return new Date(timestamp).toLocaleTimeString('cs-CZ', {hour: '2-digit', minute: '2-digit'});
};

const formatDayLabel = (timestamp) => {
	if (!timestamp) {
		return '';
	}

	const date = new Date(timestamp);

	return date.getDate() + '. ' + (date.getMonth() + 1) + '. ' + date.getFullYear();
};

function TempChartArea({data, labels, timestamps, ranges = null, color, belowZeroColor = BELOW_ZERO_COLOR, linesColor = '#bbbbbb', variant = 'default', className = '', height = '100%', xLabelMode = null, referencePoint = null, referencePoints = null, showCurrentPoint = false}) {
	const isGlassVariant = variant === 'glass';
	const chartLabelColor = isGlassVariant ? toRgba(linesColor, 0.9) : linesColor;
	const chartGridColor = isGlassVariant ? toRgba(linesColor, 0.24) : linesColor;
	const chartZeroLineColor = isGlassVariant ? toRgba(linesColor, 0.6) : linesColor;
	const chartFillTopOpacity = isGlassVariant ? 0.46 : 0.32;
	const chartFillMidOpacity = isGlassVariant ? 0.14 : 0.1;
	const chartStrokeOpacity = isGlassVariant ? 0.98 : 1;
	const chartStrokeColor = toRgba(color, chartStrokeOpacity);
	const chartBelowZeroStrokeColor = toRgba(belowZeroColor, chartStrokeOpacity);
	const chartStrokeWidth = 2;
	const getStrokeColorForValue = (value) => (parseFloat(value) < 0 ? chartBelowZeroStrokeColor : chartStrokeColor);

	var cs = {
		name: 'cs',
		options: {
			months: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'],
			shortMonths: ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'],
			days: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
			shortDays: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],
			toolbar: {
				exportToSVG: 'Stáhnout SVG',
				exportToPNG: 'Stáhnout PNG',
				exportToCSV: 'Stáhnout CSV',
				menu: 'Menu',
				selection: 'Vybrat',
				selectionZoom: 'Zoom: Vybrat',
				zoomIn: 'Zoom: Přiblížit',
				zoomOut: 'Zoom: Oddálit',
				pan: 'Přesouvat',
				reset: 'Resetovat',
			},
		},
	};

	const xLabelVisibility = (labels || []).map((label, index) => {
		if (!xLabelMode) {
			return true;
		}

		if (xLabelMode === '3h') {
			return shouldShowEvery3HoursLabel(index, timestamps, label);
		}

		if (xLabelMode === '6h') {
			return shouldShowEvery6HoursLabel(index, timestamps);
		}

		if (xLabelMode === '25h') {
			return shouldShow25HoursLabel(index, timestamps);
		}

		if (xLabelMode === 'daily') {
			return shouldShowDailyLabel(index, timestamps);
		}

		if (xLabelMode === '5days') {
			return shouldShowEvery5DaysLabel(index, timestamps);
		}

		if (xLabelMode === 'monthly') {
			return shouldShowMonthlyLabel(index, timestamps);
		}

		return true;
	});

	const displayLabels = xLabelMode
		? (labels || []).map((label, index) => {
				if (!xLabelVisibility[index]) {
					return '';
				}

				if (xLabelMode === '25h' && timestamps?.[index]) {
					return formatTimeLabel(timestamps[index]);
				}

				if (xLabelMode === 'daily' && timestamps?.[index]) {
					const date = new Date(timestamps[index]);

					return date.getDate() + '.' + (date.getMonth() + 1) + '.';
				}

				return label;
			})
		: labels;
	const chartCategories = timestamps?.length === labels?.length ? timestamps : labels;

	// Range mód (měsíc, rok): pásmo denního minima a maxima + křivka denního průměru
	const isRangeMode = Array.isArray(ranges) && ranges.length > 0;
	const averageValues = data?.[0]?.data || [];
	const rangeSeries = isRangeMode
		? [
				{
					name: 'Min - max dne',
					type: 'rangeArea',
					data: ranges.map((range, index) => ({x: chartCategories?.[index] ?? labels?.[index], y: range})),
				},
				{
					name: 'Průměr dne',
					type: 'line',
					data: averageValues.map((value, index) => ({x: chartCategories?.[index] ?? labels?.[index], y: value})),
				},
			]
		: null;

	// Hodnoty pod nulou kreslíme modře: u každé série zjistíme, kde vůči nule leží, a podle toho volíme barvu nebo gradient s přechodem na nule
	const areaSplit = getZeroSplit(getValueExtent(averageValues));
	const bandSplit = isRangeMode ? getZeroSplit(getValueExtent(ranges.flat())) : null;
	const areaBaseColor = areaSplit.mode === 'below' ? belowZeroColor : color;
	const bandBaseColor = bandSplit?.mode === 'below' ? belowZeroColor : color;
	const chartGlowColor = toRgba(areaBaseColor, isGlassVariant ? 0.34 : 0.18);
	const bandFillOpacity = isGlassVariant ? 0.26 : 0.2;
	const bandStrokeOpacity = 0.5;

	const getRangeModeColorConfig = () => {
		const isBandSplit = bandSplit.mode === 'split';
		const isAverageSplit = areaSplit.mode === 'split';

		return {
			colors: [bandBaseColor, areaBaseColor],
			stroke: {
				curve: 'straight',
				lineCap: 'round',
				width: [1, 2.5],
				colors: [toRgba(bandBaseColor, bandStrokeOpacity), toRgba(areaBaseColor, chartStrokeOpacity)],
				// stroke.fill platí jen pro sérii pásma (rangeArea), čára průměru se barví přes hlavní fill
				fill: isBandSplit ? {type: 'gradient', gradient: {type: 'vertical', colorStops: [createSplitColorStops(bandSplit.offset, color, belowZeroColor, bandStrokeOpacity)]}} : {type: 'solid'},
			},
			fill: {
				type: [isBandSplit ? 'gradient' : 'solid', isAverageSplit ? 'gradient' : 'solid'],
				opacity: [bandFillOpacity, 1],
				gradient: {
					type: 'vertical',
					colorStops: [isBandSplit ? createSplitColorStops(bandSplit.offset, color, belowZeroColor, bandFillOpacity) : [], isAverageSplit ? createSplitColorStops(areaSplit.offset, color, belowZeroColor, chartStrokeOpacity) : []],
				},
			},
		};
	};

	const getAreaModeColorConfig = () => ({
		colors: [areaBaseColor],
		stroke: {
			curve: 'smooth',
			lineCap: 'round',
			width: chartStrokeWidth,
			colors: [toRgba(areaBaseColor, chartStrokeOpacity)],
			fill: areaSplit.mode === 'split' ? {type: 'gradient', gradient: {type: 'vertical', colorStops: [createSplitColorStops(areaSplit.offset, color, belowZeroColor, chartStrokeOpacity)]}} : {type: 'solid'},
		},
		fill: {
			type: 'gradient',
			gradient: {
				shadeIntensity: 1,
				type: 'vertical',
				colorStops: [createAreaFillColorStops(areaSplit, color, belowZeroColor, chartFillTopOpacity, chartFillMidOpacity)],
			},
		},
	});

	const colorConfig = isRangeMode ? getRangeModeColorConfig() : getAreaModeColorConfig();

	const containerRef = useRef(null);
	const [chartHeight, setChartHeight] = useState(height === '100%' ? 0 : height);
	const [annotationLabelPositions, setAnnotationLabelPositions] = useState([]);

	const createPointAnnotation = (index, temp) => ({
		x: chartCategories?.[index] ?? labels?.[index],
		y: parseFloat(temp),
		marker: {
			size: 4,
			fillColor: '#f4fffa',
			strokeColor: getStrokeColorForValue(temp),
			strokeWidth: chartStrokeWidth,
			radius: 2,
		},
		label: {
			text: '',
		},
	});

	const getReferenceAnnotationItems = () => {
		const points = referencePoints || (referencePoint ? [referencePoint] : []);

		if (!points.length || !timestamps?.length) {
			return [];
		}

		return points
			.map((point, index) => {
				if (!point?.ts || point.temp === null || point.temp === undefined) {
					return null;
				}

				const referenceTime = new Date(point.ts).getTime();
				const referenceTemp = parseFloat(point.temp);

				if (!Number.isFinite(referenceTime) || Number.isNaN(referenceTemp)) {
					return null;
				}

				const nearestIndex = timestamps.reduce((bestIndex, timestamp, timestampIndex) => {
					const currentDistance = Math.abs(timestamp - referenceTime);
					const bestDistance = Math.abs(timestamps[bestIndex] - referenceTime);

					return currentDistance < bestDistance ? timestampIndex : bestIndex;
				}, 0);
				const nearestChartTemp = parseFloat(data?.[0]?.data?.[nearestIndex]);

				return {
					key: `reference-${index}`,
					annotation: createPointAnnotation(nearestIndex, Number.isNaN(nearestChartTemp) ? referenceTemp : nearestChartTemp),
					lines: [`${point.temp} °C`, formatShortDateTime(point.ts)],
				};
			})
			.filter(Boolean);
	};

	const getCurrentAnnotationItem = () => {
		if (!showCurrentPoint || !data?.[0]?.data?.length) {
			return null;
		}

		const seriesData = data[0].data;
		const currentIndex = seriesData.reduceRight((foundIndex, value, index) => {
			if (foundIndex !== -1) {
				return foundIndex;
			}

			const parsedValue = parseFloat(value);

			return Number.isNaN(parsedValue) ? -1 : index;
		}, -1);

		if (currentIndex === -1 || !timestamps?.[currentIndex]) {
			return null;
		}

		const currentTemp = seriesData[currentIndex];

		return {
			key: 'current',
			annotation: createPointAnnotation(currentIndex, currentTemp),
			lines: [`${currentTemp} °C`, formatShortDateTime(timestamps[currentIndex])],
		};
	};

	const annotationItems = [...getReferenceAnnotationItems(), getCurrentAnnotationItem()].filter(Boolean);
	const annotations = annotationItems.map((item) => item.annotation);

	const updateAnnotationLabelPositions = () => {
		if (!containerRef.current || !annotationItems.length) {
			setAnnotationLabelPositions([]);
			return;
		}

		requestAnimationFrame(() => {
			const markers = containerRef.current?.querySelectorAll('.apexcharts-point-annotations circle, .apexcharts-point-annotations path');

			if (!markers?.length || !containerRef.current) {
				setAnnotationLabelPositions([]);
				return;
			}

			const containerRect = containerRef.current.getBoundingClientRect();
			const positions = annotationItems
				.map((item, index) => {
					const marker = markers[index];

					if (!marker) {
						return null;
					}

					const markerRect = marker.getBoundingClientRect();

					return {
						key: item.key,
						left: markerRect.left - containerRect.left + markerRect.width / 2,
						top: 8,
						lines: item.lines,
					};
				})
				.filter(Boolean);

			setAnnotationLabelPositions(positions);
		});
	};

	let gr = {
		series: isRangeMode ? rangeSeries : data, // Pro více serií můžeme data předat přímo (např. data = [{name: 'Teplota', data: [...]}]
		options: {
			chart: {
				width: '100%',
				height: '100%',
				offsetY: isGlassVariant ? -2 : -15,
				offsetX: isGlassVariant ? 0 : -5,
				parentHeightOffset: 0,
				background: 'transparent',
				locales: [cs],
				defaultLocale: 'cs',
				type: isRangeMode ? 'rangeArea' : 'area',
				zoom: {enabled: false},
				stacked: false,
				toolbar: {show: false},
				events: {
					mounted: updateAnnotationLabelPositions,
					updated: updateAnnotationLabelPositions,
				},
				dropShadow: isGlassVariant
					? {
							enabled: true,
							top: 10,
							left: 0,
							blur: 16,
							color: chartGlowColor,
							opacity: 0.24,
						}
					: {enabled: false},
			},
			colors: colorConfig.colors,
			annotations: {
				// Nulová hodnota osy teploty plnou čarou (mimo rozsah osy ji ApexCharts nevykreslí)
				yaxis: [
					{
						y: 0,
						borderColor: chartZeroLineColor,
						borderWidth: 1,
						strokeDashArray: 0,
					},
				],
				points: annotations,
			},
			dataLabels: {enabled: false},
			tooltip: {
				theme: 'dark',
				fillSeriesColor: false,
				style: {fontSize: isGlassVariant ? '1.1rem' : '1rem'},
				// Klíče platné jen pro range mód rozbalujeme podmíněně - předat je s hodnotou undefined by přepsalo výchozí konfiguraci ApexCharts
				...(isRangeMode
					? {
							shared: true,
							intersect: false,
							y: {formatter: (value) => (value === null || value === undefined ? '-' : value + ' °C')},
						}
					: {}),
				x: {
					show: true,
					formatter: (_val, opts) => {
						const index = opts?.dataPointIndex;

						if (typeof index === 'number' && timestamps?.[index]) {
							return isRangeMode ? formatDayLabel(timestamps[index]) : formatTooltipDateTime(timestamps[index]);
						}

						return labels?.[index] ?? _val;
					},
				},
			},
			stroke: colorConfig.stroke,
			fill: colorConfig.fill,
			markers: {
				size: isGlassVariant ? 0 : 0,
				hover: {
					size: isGlassVariant ? 5 : 4,
					sizeOffset: 2,
				},
				strokeColors: '#f4fffa',
				strokeWidth: 2,
			},
			title: {text: '', align: 'left'},
			subtitle: {text: '', align: 'left'},
			...(isRangeMode ? {} : {labels: chartCategories}),
			legend: isRangeMode
				? {
						show: true,
						position: 'top',
						horizontalAlign: 'right',
						offsetY: 4,
						fontSize: '1.05rem',
						labels: {colors: chartLabelColor},
						markers: {size: 6},
						itemMargin: {horizontal: 8},
					}
				: {show: false},
			xaxis: {
				...(isRangeMode ? {type: 'category'} : {}),
				tooltip: {enabled: false},
				crosshairs: {
					show: true,
					tooltip: {enabled: false},
				},
				axisBorder: {show: false},
				overwriteCategories: xLabelMode ? displayLabels : undefined,
				labels: {
					formatter: function (value, _timestamp, opts) {
						const index = typeof opts?.i === 'number' ? opts.i : opts?.dataPointIndex;

						if (typeof index === 'number' && !xLabelVisibility[index]) {
							return '';
						}

						return displayLabels?.[index] ?? labels?.[index] ?? value;
					},
					show: true,
					hideOverlappingLabels: true,
					rotate: 0,
					offsetY: -2,
					style: {fontSize: isGlassVariant ? '1.05rem' : '1rem', colors: [chartLabelColor]},
				},
				axisTicks: {
					show: false,
					tickAmount: 6,
				},
			},
			yaxis: {
				labels: {
					show: true,
					rotate: 0,
					style: {fontSize: isGlassVariant ? '1.05rem' : '1rem', colors: [chartLabelColor]},
				},
			},
			grid: {
				show: true,
				position: 'back',
				borderColor: chartGridColor,
				strokeDashArray: isGlassVariant ? 6 : 0,
				padding: isGlassVariant ? {left: 6, right: -6, top: annotations.length ? 44 : 8, bottom: -5} : {top: annotations.length ? 44 : 0, bottom: -5},
				xaxis: {lines: {show: false}},
				yaxis: {lines: {show: true}},
			},
			noData: {
				text: 'Zatím bez dat',
				align: 'center',
				verticalAlign: 'middle',
				style: {
					color: chartLabelColor,
					fontSize: isGlassVariant ? '1.25rem' : '1.1rem',
				},
			},
		},
	};

	useLayoutEffect(() => {
		if (height !== '100%' || !containerRef.current) {
			setChartHeight(height);
			return undefined;
		}

		const element = containerRef.current;

		const updateHeight = () => {
			const nextHeight = element.clientHeight;
			if (nextHeight > 0) {
				setChartHeight(nextHeight);
			}
		};

		updateHeight();

		const resizeObserver = new ResizeObserver(updateHeight);
		resizeObserver.observe(element);

		return () => resizeObserver.disconnect();
	}, [height, data, labels]);

	useLayoutEffect(() => {
		updateAnnotationLabelPositions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chartHeight, referencePoint, referencePoints, showCurrentPoint, data, labels]);

	const wrapperStyle = height === '100%' ? {height: '100%', minHeight: 0, flex: '1 1 auto', position: 'relative'} : {position: 'relative'};

	return (
		<div ref={containerRef} className={className} style={wrapperStyle}>
			{chartHeight ? <ReactApexChart options={gr.options} series={gr.series} type={isRangeMode ? 'rangeArea' : 'area'} width="100%" height={chartHeight} /> : null}
			{annotationLabelPositions.map((position) => (
				<div key={position.key} className="temperature-chart-reference-label" style={{left: position.left, top: position.top}}>
					<span>{position.lines[0]}</span>
					<span>{position.lines[1]}</span>
				</div>
			))}
		</div>
	);
}

export default memo(TempChartArea);
